<template>
  <div :id="id + '_rep'" :class="_repeater_class">
    <component
      :is="fieldset ? 'label' : 'h4'"
      v-if="title && title.trim().length > 0"
      :id="id + '_rep_lbl'"
      class="mbt"
      data-label-type="repeater-title"
      tabindex="-1"
      >{{ title }}
    </component>
    <cat-hint v-if="hint" :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="inlineHelp && inlineHelp.text"
      :id="id + '_help'"
      :heading="inlineHelp.title"
      :text="inlineHelp.text"
      :aria-describedby="
        title && title.trim().length > 0 ? id + '_rep_lbl' : null
      "
    />
    <cat-errors
      v-if="errors"
      :id="id"
      :errors="errors"
      :container="!fieldset"
    />

    <div v-if="$slots.filters" class="repeater-filters">
      <slot name="filters" />
    </div>

    <div
      v-if="!pagingbottom"
      class="grid-x full grid-margin-x"
      :class="{ 'search-header': exportOptions }"
    >
      <div class="cell large-auto stack-horizontally stack-align-middle">
        <cat-pagination
          v-if="paging"
          :id="id"
          :paging="paging"
          :pagination="pagination"
          class=""
          @ui-repeater-pagination-update="pageSelect"
        />
      </div>
      <div
        class="cell text-size-medium stack-horizontally stack-align-top large-shrink"
      >
        <cat-sort
          v-if="sortOptions && rows.length > 0"
          :sort-options="sortOptions"
          :selected-index="selectedSort"
          @ui-select-sort="selectSort"
        />
        <div v-if="exportOptions" class="stack-horizontally stack-align-middle">
          <inline-menu
            :id="id + '-export'"
            small
            hollow
            button
            link
            :items="
              exportOptions.map(function (item) {
                return {
                  text: item.title,
                  value: item.type,
                  url: item.url,
                  isLink: item.navigationType === 'link'
                };
              })
            "
            :menu-button-id="id + '-export-btn'"
            :offscreen-label="
              resolveTxt('skin.results.export', 'Export results')
            "
            @ui-select="exportUrl($event)"
          >
            <template #trigger>
              <span>
                <i class="icon-doc-text"></i
                >{{ resolveTxt("skin.results.export", "Export results") }}
              </span>
            </template>
          </inline-menu>
        </div>
      </div>
    </div>
    <div ref="tableWrapper" :class="_wrapper_class">
      <div v-if="dataTable" class="shadowL"></div>
      <div v-if="dataTable" class="shadowR"></div>
      <table v-if="rows.length > 0" ref="table" :class="_table_class">
        <thead>
          <tr>
            <th
              v-for="(column, idx) in _columns"
              :key="idx"
              :style="'width:' + column.width + ';'"
              :class="column.css"
            >
              {{ column.colname }}
            </th>
          </tr>
        </thead>
        <tbody>
          <slot>
            <tr
              v-for="(row, ridx) in rows"
              :key="ridx + 1"
              :data-label="rowCaptionPrefix + ' ' + (ridx + 1)"
            >
              <td
                v-for="(item, cidx) in row"
                :key="cidx + 1"
                :class="
                  _columns[cidx] && _columns[cidx].css ? _columns[cidx].css : ''
                "
                :data-label="
                  responsiveHeadings && _columns[cidx] && _columns[cidx].colname
                    ? _columns[cidx].colname
                    : false
                "
              >
                {{ item }}
              </td>
            </tr>
          </slot>
        </tbody>
        <tfoot v-if="footer">
          <tr v-if="useFooterSlot">
            <slot name="footerSlot"></slot>
          </tr>
          <tr v-else>
            <th
              v-for="(column, idx) in _columns"
              :key="idx + 1"
              :class="column.css"
            ></th>
          </tr>
        </tfoot>
      </table>
    </div>
    <div v-if="pagingbottom" class="grid-x pn flex-align-top mts">
      <div class="cell medium-auto small-12">
        <cat-pagination
          v-if="pagingbottom"
          :id="id"
          :paging="paging"
          :pagination="pagination"
          class=""
          @ui-repeater-pagination-update="pageSelect"
        />
      </div>
      <div class="cell medium-shrink pbt-mob small-12">
        <cat-sort
          v-if="sortOptions && rows.length > 0"
          :sort-options="sortOptions"
          :selected-index="selectedSort"
          @ui-select-sort="selectSort"
        />
      </div>
    </div>

    <cat-repeater-callout
      v-if="callout"
      :id="id + '_selected'"
      :open="childDetail"
      :close-caption="closeText"
      :dialog="modal"
      :size="modalSize"
      :callout-label="calloutLabel"
      :panel-close="modalClose"
      @ui-repeater-done="handleChildDone"
    >
      <slot name="selected"></slot>
      <template #buttons>
        <slot v-if="childDetail" name="buttons"></slot>
      </template>
    </cat-repeater-callout>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import $ from "jquery";
import { defaultResolveTxt } from "@/components/field-element";

import CatErrors from "@/components/catErrors/catErrors.vue";
import CatPagination from "./catPagination";
import CatRepeaterCallout from "./catRepeaterCallout";
import CatHelp from "@/components/utilities/help.vue";
import CatHint from "@/components/utilities/hint";
import CatSort from "@/components/catSearch/catRepeaterSort";
import InlineMenu from "@/components/catMenu/catMenu.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatRepeaterTable",
  components: {
    InlineMenu,
    CatErrors,
    CatPagination,
    CatRepeaterCallout,
    CatHelp,
    CatHint,
    CatSort
  },
  props: {
    id: {
      type: [String, Number]
    },
    title: [String],
    fieldset: [Boolean],
    errors: [Array],
    readonly: [Boolean],
    childrenHasErrors: [Boolean],
    paging: {
      type: Boolean,
      default: false
    },
    pagingbottom: {
      type: Boolean,
      default: false
    },
    pagination: [Object],
    numColumns: [Number],
    columnNames: [Array],
    columnClasses: {
      type: Array,
      default: function () {
        return [];
      }
    },
    columnAlignment: [Array],
    controlColumn: [Boolean],
    plain: [Boolean],
    bordered: [Boolean],
    compact: [Boolean],
    striped: [Boolean],
    tableSm: [Boolean],
    tableMd: [Boolean],
    columnWidths: [Array],
    rows: [Array],
    rowCaptionPrefix: {
      type: String,
      default: "Row",
      description: "The prefix to caption the row index eg. 'Row 1'"
    },
    withToggle: {
      type: Boolean,
      default: false,
      description: "Style table with toggle details"
    },
    footer: {
      type: Boolean,
      default: false
    },
    useFooterSlot: {
      type: Boolean,
      default: false
    },
    responsiveHeadings: {
      type: Boolean,
      default: true
    },
    dataTable: {
      type: Boolean,
      default: false,
      description:
        "Doesn't allow text wrapping. Intended to be used for tables with numbers that shouldn't wrap."
    },
    tableWrapperClasses: {
      type: Array,
      description:
        "Any additional classes to be added to the parent of the table markup."
    },
    sortOptions: {
      type: Array
    },
    selectedSort: {
      type: Number,
      default: 0
    },
    exportOptions: {
      type: Array
    },
    callout: {
      type: Boolean,
      default: false
    },
    calloutLabel: [String],
    closeText: [String],
    modal: {
      type: Boolean,
      default: false,
      description:
        "Displays the callout as a modal window rather then slideout - see modalSize prop for sizing "
    },
    modalSize: {
      type: [String],
      description:
        "'xl' will result in class 'modal-size-xl' - predefined sizes are 's, m, l, xl'"
    },
    modalClose: {
      type: [Boolean],
      description:
        "Controls if a 'close' button is shown for the selected record."
    },
    calloutOpen: {
      type: Boolean,
      default: false
    },
    inlineHelp: {
      type: Object,
      description: "Inline help details for this repeater under the main title."
    },
    hint: {
      type: String,
      description: "Hint appearing under the main title"
    }
  },
  watch: {
    calloutOpen: function (open) {
      this.childDetail = open;
    }
  },
  data: function () {
    return {
      childDetail: this.calloutOpen
    };
  },
  computed: {
    _num_columns: function () {
      if (this.numColumns) {
        return this.numColumns;
      } else if (this.columnNames) {
        return this.columnNames.length;
      } else if (this.rows && this.rows.length) {
        return this.rows[0].length;
      }
      return null;
    },
    _wrapper_class: function () {
      var css = [];
      if (this.dataTable) {
        css.push("dataTableWrapper");
      }
      if (this.tableWrapperClasses && this.tableWrapperClasses.length > 0) {
        css = css.concat(this.tableWrapperClasses);
      }
      return css;
    },
    _table_class: function () {
      return {
        table: true,
        ctrl: this.controlColumn,
        plain: this.plain,
        bordered: this.bordered,
        compact: this.compact,
        striped: this.striped,
        "with-toggle": this.withToggle,
        "table-sm": this.tableSm,
        "table-md": this.tableMd,
        dataTable: this.dataTable
      };
    },
    _repeater_class: function () {
      return {
        "cat-repeater cat-repeater-table ": true,
        "fieldset full": this.fieldset,
        "no-padding": true,
        invalid:
          (this.errors && this.errors.length > 0) || this.childrenHasErrors
      };
    },
    _columns: function () {
      var result = [];
      for (var i = 0; i < this._num_columns; i++) {
        const alignment = this.columnAlignment && this.columnAlignment[i];
        let css =
          this.columnClasses && this.columnClasses[i]
            ? this.columnClasses[i]
            : "";
        if (alignment) {
          css += ` text-align-${alignment}`;
        }
        result.push({
          colname: this.columnNames ? this.columnNames[i] : null,
          width: this.columnWidths ? this.columnWidths[i] : null,
          css: css
        });
      }
      return result;
    },
    modalClass: function () {
      return {
        modal: this.modal,
        ["modal-size-" + this.modalSize]: this.modalSize
      };
    }
  },
  methods: {
    pageSelect: function (event) {
      this.$emit("ui-repeater-pagination-update", event);
    },
    selectSort: function (event) {
      this.$emit("ui-sort-select", this.sortOptions[event.index]);
    },
    toggleRepeaterChild: function () {
      this.childDetail = !this.childDetail;
    },
    handleChildDone: function (event) {
      this.toggleRepeaterChild();
      this.$emit("ui-repeater-done", event);
    },
    setTableScrollClasses: function (tableEl) {
      if (!tableEl) {
        return;
      }

      var tableWidth = $(tableEl).find("thead tr").width();
      //plus 10 to ensure PCs remove the overflow-x
      var columnWidth = $(tableEl).parent().width() + 10;

      if (columnWidth >= tableWidth) {
        // $(tableEl).css("overflow-x", "hidden");
        $(tableEl)
          .parent()
          .removeClass("scroll-right")
          .removeClass("scroll-left");
      } else {
        // $(tableEl).css("overflow-x", "scroll");
        $(tableEl).parent().addClass("scroll-right");
      }
    },
    tableScroll: function (event) {
      const t = this;
      this.$nextTick(function () {
        const tableEl = t.$refs.table;

        // determine if the width of the tr is less than the width of the column and add/remove overflow-x and right shadow appropriately
        // mainly for PC users so it doesn't show the scrollbar when its not neccessary

        //on load & then on resize do the check
        t.setTableScrollClasses(tableEl);

        $(window).on("resize", function () {
          t.setTableScrollClasses(tableEl);
        });

        // on table scroll display the appropriate shadows
        $(tableEl).on("scroll", function () {
          var parent_div = $(tableEl).parent();
          var scrollWidth = $(tableEl)[0].scrollWidth;

          if (
            $(tableEl).scrollLeft() + $(tableEl).innerWidth() >=
            scrollWidth
          ) {
            if (parent_div.hasClass("scroll-right")) {
              parent_div.removeClass("scroll-right");
            }
          } else if ($(tableEl).scrollLeft() === 0) {
            if (parent_div.hasClass("scroll-left")) {
              parent_div.removeClass("scroll-left");
            }
          } else {
            if (!parent_div.hasClass("scroll-right")) {
              parent_div.addClass("scroll-right");
            }
            if (!parent_div.hasClass("scroll-left")) {
              parent_div.addClass("scroll-left");
            }
          }
        });
      });
    },
    exportUrl: function (exportOption) {
      window.location.href = exportOption.item.url;
    }
  },
  mounted: function () {
    if (this.dataTable) {
      this.tableScroll();
    }
  },
  updated: function () {
    // TODO: If mounted works on the calculation of the tableWidth and parentWidth,
    //  we don't need this since updated gets called on each hover
    if (this.dataTable) {
      this.tableScroll();
    }
  }
});
</script>
